import { Box, CircularProgress, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

type ListToolbarFrameProps = {
    /** The views of the list, beside the title. */
    tabs?: ReactNode;
    /** A count beside the views; empty while unknown. */
    count?: ReactNode;
    /** Dims the count and shows a spinner beside it while the list re-runs. */
    isFetching: boolean;
    /** The list's own actions, at the end of the title line. */
    actions?: ReactNode;
    /** Rows of controls under the title line, each wrapping only when the window cannot hold it. */
    children?: ReactNode;
};

/**
 * The block above a patient list: the list's title, the views and its actions
 * on one line, then the rows of controls that narrow it.
 *
 * The refresh indicator sits in the place of the count it is replacing, so
 * pressing a control never shifts the controls the user is pressing.
 */
export default function ListToolbarFrame({
    tabs,
    count,
    isFetching,
    actions,
    children,
}: ListToolbarFrameProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                px: 3,
                pt: 1,
                pb: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    minHeight: 44,
                }}
            >
                <Typography variant="h6" component="h1">
                    <FormattedMessage id="patientList.title" />
                </Typography>
                {tabs}
                {/* The previous count stays, dimmed, while the query
                    re-runs: a count that vanished and came back would
                    flicker, and the spinner beside it says why it is stale. */}
                <Typography
                    variant="body2"
                    sx={{
                        opacity: isFetching ? 0.5 : 1,
                        transition: 'opacity 150ms',
                    }}
                >
                    {count}
                </Typography>
                {isFetching && <CircularProgress size={14} />}
                <Box sx={{ flex: 1 }} />
                {actions}
            </Box>
            {children}
        </Box>
    );
}
